import {
  ArrowRight,
  ArrowUpRight,
  CreditCard,
  Laptop,
  MonitorSmartphone,
  ShieldCheck,
  Smartphone,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { haloAccount } from "@/config/haloAccount";

const accountFeatures = [
  {
    name: "Your plan",
    body: "See your plan, subscription status and next renewal at a glance.",
    icon: CreditCard,
  },
  {
    name: "Your devices",
    body: "See every protected device and free a place whenever you need to.",
    icon: MonitorSmartphone,
  },
  {
    name: "Your downloads",
    body: "Install Halo SuperVPN for every supported platform from one clear place.",
    icon: Laptop,
  },
  {
    name: "Your support",
    body: "Get account help, manage privacy choices and request account deletion.",
    icon: ShieldCheck,
  },
];

const connectedStores = [
  { name: "Microsoft Store", platforms: "Windows", icon: Laptop },
  { name: "Apple App Store", platforms: "iPhone and iPad", icon: Smartphone },
  { name: "Mac App Store", platforms: "macOS", icon: Laptop },
  { name: "Google Play", platforms: "Android", icon: MonitorSmartphone },
];

export default function HaloAccount() {
  return (
    <>
      <SEO
        title="Halo Account and Billing — LightningFlow"
        description="Manage your Halo SuperVPN subscription, billing source, devices and account deletion."
        noIndex
      />
      <section className="relative overflow-hidden border-b border-ink-100 bg-ink-50/50">
        <div className="absolute inset-0 lf-grid-bg-faint pointer-events-none" />
        <div className="lf-container relative grid gap-10 py-16 md:py-24 lg:grid-cols-[1fr_.62fr] lg:items-end">
          <div>
            <div className="lf-eyebrow">Halo Account &amp; Billing</div>
            <h1 className="mt-5 max-w-4xl font-display text-5xl font-black leading-[.96] tracking-[-.035em] md:text-6xl">
              Your Halo SuperVPN. All in one place.
            </h1>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-ink-600 md:text-xl">
              Manage your Halo SuperVPN subscription, billing provider and account
              support from one clear place.
            </p>
          </div>
          <div className="border border-ink-100 bg-white p-7">
            <ShieldCheck className="h-7 w-7 text-electric" strokeWidth={1.6} />
            <div className="mt-7 font-mono text-[10px] uppercase tracking-[.2em] text-ink-500">
              Halo Account
            </div>
            <p className="mt-2 font-display text-2xl font-bold">
              {haloAccount.portalEnabled ? "Welcome back." : "One sign-in. Complete control."}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-ink-500">
              {haloAccount.portalEnabled
                ? "Sign in to see your Halo SuperVPN plan and devices."
                : "Halo Account opens with Halo SuperVPN, bringing your plan, devices, downloads and support together."}
            </p>
            {haloAccount.portalEnabled ? (
              <a
                className="lf-btn-primary mt-6"
                href={haloAccount.accountPortalUrl}
                rel="noreferrer"
                data-testid="halo-account-sign-in"
              >
                Sign in to Halo Account <ArrowUpRight className="h-4 w-4" />
              </a>
            ) : (
              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-[.2em] text-electric">
                  Available when Halo SuperVPN launches
                </div>
                <Link className="lf-link mt-4" to="/halo">
                  Explore Halo SuperVPN <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="account-features" className="lf-section scroll-mt-24">
        <div className="lf-container">
          <div className="max-w-3xl">
            <div className="lf-eyebrow">Everything together</div>
            <h2 className="mt-4 font-display text-4xl font-black leading-tight">
              Everything you need. One elegant account.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-600">
              Halo Account is designed around the things that matter after you
              subscribe—your plan, your protection and your devices.
            </p>
          </div>

          <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-2 xl:grid-cols-4">
            {accountFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <article key={feature.name} className="bg-white p-7">
                  <Icon className="h-6 w-6 text-electric" strokeWidth={1.7} />
                  <h3 className="mt-7 font-display text-2xl font-bold">{feature.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-600">
                    {feature.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-ink-900 text-white">
        <div className="lf-container py-16 md:py-20">
          <div className="grid gap-8 lg:grid-cols-[.72fr_1.28fr] lg:items-end">
            <div>
              <div className="lf-eyebrow text-cyan-200">Connected subscriptions</div>
              <h2 className="mt-4 font-display text-4xl font-black leading-tight md:text-5xl">
                Works with the Store you choose.
              </h2>
            </div>
            <p className="max-w-2xl text-lg leading-relaxed text-slate-200">
              Subscribe through Microsoft, Apple or Google. Halo Account brings
              your plan status and protected devices into one clear view.
            </p>
          </div>

          <div className="mt-10 grid gap-px border border-white/15 bg-white/15 sm:grid-cols-2 xl:grid-cols-4">
            {connectedStores.map((store) => {
              const Icon = store.icon;
              return (
                <article key={store.name} className="bg-ink-900 p-7">
                  <Icon className="h-6 w-6 text-cyan-300" strokeWidth={1.7} />
                  <div className="mt-7 font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">
                    {store.platforms}
                  </div>
                  <h3 className="mt-2 font-display text-xl font-bold">{store.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">
                    Linked to Halo Account
                  </p>
                </article>
              );
            })}
          </div>

          <p className="mt-6 max-w-4xl text-sm leading-relaxed text-slate-300">
            For Store purchases, payment methods, receipts and cancellations
            remain securely managed by that Store.
          </p>
        </div>
      </section>

      <section className="lf-section">
        <div className="lf-container grid gap-px bg-ink-100 md:grid-cols-2">
          <article className="border border-ink-100 bg-white p-7 md:p-10">
            <ShieldCheck className="h-6 w-6 text-electric" />
            <div className="lf-eyebrow mt-7">Need help?</div>
            <h2 className="mt-3 font-display text-3xl font-black">Halo SuperVPN support.</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-600">
              Get help with your account, subscription or protected devices.
              Never send card numbers, passwords or Store credentials.
            </p>
            <a
              className="lf-link mt-6"
              href={`mailto:${haloAccount.supportEmail}?subject=Halo%20Account%20support`}
            >
              Contact Halo SuperVPN support <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </article>
          <article className="border border-ink-100 bg-white p-7 md:p-10">
            <Trash2 className="h-6 w-6 text-electric" />
            <div className="lf-eyebrow mt-7">Privacy control</div>
            <h2 className="mt-3 font-display text-3xl font-black">Delete your account.</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-600">
              Start account deletion from the web, even if Halo SuperVPN is no longer
              installed. Store subscriptions must be cancelled separately.
            </p>
            <Link className="lf-link mt-6" to="/halo/account/delete">
              Account deletion details <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </article>
        </div>
      </section>
    </>
  );
}
